import { HTML5toTouch } from "rdndmb-html5-to-touch";
import { useEffect, useState } from "react";
import { DndProvider } from "react-dnd-multi-backend";
import "../App.css";
import { DraggableMessage } from "../components/Draggables";
import {
  COLOR_DARK,
  COLOR_DIVISOR,
  COLOR_HIGHLIGHTED,
  FONT,
  REGEX_VALID_ROMAN,
  ROMAN_FONT,
  ROMAN_SYSTEM
} from "../defaults";
import { convertToAlternate, convertToDecimal, increaseProgress } from "../helpers";
import {
  buttonCheck,
  InputBoxRemainder,
  levelDivisionTask1,
  levelDivisionTask2,
  level4RomanInputs,
  level4RomanRemainders,
  level4RomanRemainderSmaller1,
  level4RomanRemainderSmaller2,
  level4RomanRemainderSmaller3,
  levelCorrect,
  levelIncorrect,
  levelNoInputMulti
} from "../strings";

const LevelNumber = 4;

// The ranges of multiplicands, multipliers and remainders produce a range for the quotient of 5-19.
function calculateMultiplicands(currentProgress) {
  const numbers = [2, 4, 3];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * 2) + 2; // Range 2-4
  }
  return numbers[currentProgress];
}

function calculateMultipliers(currentProgress) {
  const numbers = [4, 4, 2];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * 2) + 2; // Range 2-4
  }
  return numbers[currentProgress];
}

// Make sure there is always a remainder, i.e. it is never zero.
function calculateRemainders(multiplicand, currentProgress) {
  const numbers = [1, 3, 1];
  if (currentProgress >= numbers.length) {
    return Math.round(Math.random() * (multiplicand-2)) + 1; // Range 1-3 (1-multiplicand)
  }
  return numbers[currentProgress];
}


function Level4Roman({levelProgress, setLevelProgress, showMessage, setShowMessage, disableInput, setDisableInput}) {
  const [multiplicand, setMultiplicand] = useState(
    calculateMultiplicands(levelProgress[LevelNumber])
  );
  const [multiplier, setMultiplier] = useState(
    calculateMultipliers(levelProgress[LevelNumber])
  );
  const [remainder, setRemainder] = useState(
    calculateRemainders(multiplicand, levelProgress[LevelNumber])
  );
  const [message, setMessage] = useState("");
  const [correct, setCorrect] = useState(false);

  useEffect(() => {
    const mult = calculateMultiplicands(levelProgress[LevelNumber]);
    setMultiplicand(mult);
    setMultiplier(calculateMultipliers(levelProgress[LevelNumber]));
    setRemainder(calculateRemainders(mult, levelProgress[LevelNumber]));
    setInputMultiplier("");
    setInputQuotient("");
    setInputRemainder1("");
    setInputRemainder2("");
  }, [levelProgress]);

  const [inputMultiplier, setInputMultiplier] = useState("");
  const [inputQuotient, setInputQuotient] = useState("");
  const [inputRemainder1, setInputRemainder1] = useState("");
  const [inputRemainder2, setInputRemainder2] = useState("");

  const checkAnswers = () => {
    const isMultiplierCorrect = convertToDecimal(ROMAN_SYSTEM, inputMultiplier) === multiplier;
    const isQuotientCorrect = convertToDecimal(ROMAN_SYSTEM, inputQuotient) === multiplier;
    const isRemainder1Correct = convertToDecimal(ROMAN_SYSTEM, inputRemainder1) === remainder;
    const isRemainder2Correct = convertToDecimal(ROMAN_SYSTEM, inputRemainder2) === remainder;
    const isCorrect = isMultiplierCorrect && isQuotientCorrect && isRemainder1Correct && isRemainder2Correct;
    setCorrect(isCorrect);
    if (isCorrect) {
      setMessage(levelCorrect)
    } else if (
      inputMultiplier === "" ||
      inputQuotient === "" ||
      inputRemainder1 === "" ||
      inputRemainder2 === ""
    ) {
      setMessage(levelNoInputMulti);
    } else if (inputMultiplier !== inputQuotient) {
      setMessage(level4RomanInputs);
    } else if (inputRemainder1 !== inputRemainder2) {
      setMessage(level4RomanRemainders);
    } else if (parseInt(convertToDecimal(ROMAN_SYSTEM, inputRemainder1)) >= multiplicand) {
      setMessage(
        <div>
          {level4RomanRemainderSmaller1}
          <span style={{ color: COLOR_DIVISOR }}>
            {" "}
            {level4RomanRemainderSmaller2}{" "}
          </span>{" "}
          {level4RomanRemainderSmaller3}{" "}
        </div>
      );
      setInputRemainder1("");
      setInputRemainder2("");
    } else if (isRemainder1Correct) {
      setMessage(levelIncorrect);
      setInputMultiplier("");
      setInputQuotient("");
    } else if (isMultiplierCorrect) {
      setMessage(levelIncorrect);
      setInputRemainder1("");
      setInputRemainder2("");
    } else {
      setMessage(levelIncorrect);
      setInputMultiplier("");
      setInputQuotient("");
      setInputRemainder1("");
      setInputRemainder2("");
    }
    displayMessage();
  };

  const displayMessage = () => {
    setDisableInput(true);
    setShowMessage(true);
  };

  const handleCloseMessage = () => {
    if (correct) {
      increaseProgress(levelProgress, setLevelProgress, LevelNumber, ROMAN_SYSTEM);
      setInputMultiplier("");
      setInputQuotient("");
      setInputRemainder1("");
      setInputRemainder2("");
    }
    setDisableInput(false);
    setMessage("");
  };

  const inputStyle = {
    color: "white",
    fontSize: "6dvh",
    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    borderRadius: "0.25em",
    textAlign: "center",
    maxWidth: "25dvh",
    fontFamily: ROMAN_FONT,
    color: COLOR_DARK,
    border: "0.2dvh solid black",
  };

  // Height + 2*top must be 100% so that it is centered nicely.
  const containerWrapperStyle = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "absolute",
    top: "20%",
    width: "100%",
    height: "60%",
  }

  const containerStyle = {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    textShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    fontFamily: FONT,
    padding: "10dvh",
    borderRadius: "0.5em",
    boxShadow: "0.3svh 0.3svh 0.6svh rgba(0, 0, 0, 0.5)",
    maxWidth: "80%",
    margin: "0 auto",
    justifyContent: "center",
    backgroundColor: COLOR_DARK,
    border: "0.2dvh solid black"
  };

  const rowStyle = {
    display: "flex",
    alignItems: "center",
    fontSize: "6dvh",
  };

  return (
    <div>
      <div
        className="Welcome-label"
        style={{ fontFamily: FONT, fontSize: "4dvh" }}
      >
        {levelDivisionTask1}
        <span style={{fontFamily: ROMAN_FONT, color: COLOR_HIGHLIGHTED, fontSize: "5dvh"}}>{convertToAlternate(ROMAN_SYSTEM, multiplicand * multiplier + remainder)}</span>
        {levelDivisionTask2}
        <span style={{ color: COLOR_DIVISOR }}>{multiplicand}</span>?<br></br>
      </div>
      <div style={containerWrapperStyle}>
        <div style={containerStyle}>
          <div style={{...rowStyle, marginBottom: "5dvh"}}>
            <span style={{fontFamily: ROMAN_FONT, marginTop: "0.8dvh", color: COLOR_HIGHLIGHTED}}>{convertToAlternate(ROMAN_SYSTEM, multiplicand * multiplier + remainder)}</span>&nbsp;&nbsp;:&nbsp;&nbsp;
            <span style={{ color: COLOR_DIVISOR }}>{multiplicand}</span>
            &nbsp;&nbsp;=&nbsp;&nbsp;
            <input
              className="input-field"
              type="text"
              disabled={disableInput}
              value={inputQuotient}
              pattern={REGEX_VALID_ROMAN}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
                setInputQuotient(e.target.value);}
              }
              maxLength={5}
              style={{
                ...inputStyle,
                backgroundColor: "aquamarine"
              }}
            />
            &nbsp;&nbsp;{InputBoxRemainder}&nbsp;&nbsp;
            <input
              className="input-field"
              type="text"
              disabled={disableInput}
              value={inputRemainder2}
              pattern={REGEX_VALID_ROMAN}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
                setInputRemainder2(e.target.value);}
              }
              maxLength={5}
              style={{
                ...inputStyle,
                backgroundColor: "lightgreen"
              }}
            />
          </div>

          <div style={rowStyle}>
            <span style={{ color: COLOR_DIVISOR }}>{multiplicand}</span>
            &nbsp;&nbsp;x&nbsp;&nbsp;
            <input
              className="input-field"
              type="text"
              disabled={disableInput}
              value={inputMultiplier}
              pattern={REGEX_VALID_ROMAN}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
                setInputMultiplier(e.target.value);}
              }
              maxLength={5}
              style={{
                ...inputStyle,
                backgroundColor: "aquamarine"
              }}
            />
            &nbsp;&nbsp;+&nbsp;&nbsp;
            <input
              className="input-field"
              type="text"
              disabled={disableInput}
              value={inputRemainder1}
              pattern={REGEX_VALID_ROMAN}
              onChange={(e) => {
                e.target.value = e.target.value.replace(/\s/g, ''); // LLM-generated. Disallow white-space input.
                setInputRemainder1(e.target.value);}
              }
              maxLength={5}
              style={{
                ...inputStyle,
                backgroundColor: "lightgreen"
              }}
            />
            &nbsp;&nbsp;=&nbsp;&nbsp;<span style={{fontFamily: ROMAN_FONT, marginTop: "0.8dvh", color: COLOR_HIGHLIGHTED}}>{convertToAlternate(ROMAN_SYSTEM, multiplicand * multiplier + remainder)}</span>
          </div>
        </div>
      </div>
      
      <button className="checkAnswer-button" onClick={checkAnswers}>
        {buttonCheck}
      </button>
      <DndProvider options={HTML5toTouch}>
      {showMessage && <DraggableMessage show={showMessage} setShow={setShowMessage} handleClose={handleCloseMessage} message={message}/>} {/* Conditionally rendering it with showMessage makes the message re-render, and hence center itself, every time. */}
      </DndProvider>
    </div>
  );
}

export default Level4Roman;
